import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { Search, Menu } from 'lucide-react';
import { useVpsStore } from '../../stores/vpsStore';
import { useJobStore } from '../../stores/jobStore';
import { useSearchStore } from '../../stores/searchStore';
import { useUiStore } from '../../stores/uiStore';
import SearchModal from './SearchModal';
import QuickActionsMenu from './QuickActionsMenu';

const PAGE_TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/jobs': 'Jobs',
  '/tasks': 'Tasks',
  '/activity': 'Activity',
  '/calendar': 'Calendar',
  '/projects': 'Projects',
  '/content': 'Content',
  '/approvals': 'Approvals',
  '/agents': 'Agents',
  '/organization': 'Org Chart',
  '/office': 'Office',
  '/memory': 'Memory',
  '/docs': 'Docs',
  '/vps': 'VPS Status',
};

function formatSyncTime(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  try {
    return `Synced ${formatDistanceToNow(parseISO(iso), { addSuffix: false })} ago`;
  } catch {
    return 'Synced recently';
  }
}

export default function Header() {
  const { pathname } = useLocation();
  const title = PAGE_TITLES[pathname] ?? 'Mission Control';

  const vpsStatus = useVpsStore((s) => s.status);
  const syncStatus = useVpsStore((s) => s.syncStatus);
  const stats = useJobStore((s) => s.stats);

  const setSearchOpen = useSearchStore((s) => s.setOpen);
  const setMobileSidebarOpen = useUiStore((s) => s.setMobileSidebarOpen);

  const connected = vpsStatus?.connected ?? false;
  const todayCost = stats?.todayCost ?? 0;
  const lastSync = syncStatus?.lastFromVps ?? syncStatus?.lastToVps ?? null;

  // Cmd+K keyboard shortcut
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setSearchOpen]);

  return (
    <>
      <header className="flex items-center justify-between h-14 px-3 sm:px-4 md:px-6 border-b border-border-subtle flex-shrink-0 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {/* Hamburger — mobile only */}
          <button
            className="flex md:hidden items-center justify-center w-8 h-8 rounded-md text-text-muted hover:text-text-primary hover:bg-white/5 transition-colors flex-shrink-0"
            onClick={() => setMobileSidebarOpen(true)}
            aria-label="Open navigation"
          >
            <Menu size={20} />
          </button>
          <h1 className="text-base sm:text-lg font-semibold text-text-primary truncate">{title}</h1>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
          {/* Search button */}
          <button
            className="flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-lg text-xs text-text-muted hover:text-text-secondary bg-bg-tertiary hover:bg-border-subtle border border-border-subtle transition-colors"
            onClick={() => setSearchOpen(true)}
          >
            <Search className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Search...</span>
            <kbd className="hidden md:inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-bg-secondary border border-border-subtle rounded ml-2">
              {navigator.platform?.includes('Mac') ? '\u2318' : 'Ctrl'}K
            </kbd>
          </button>

          {/* Quick Actions */}
          <QuickActionsMenu />

          {/* Divider — hidden on mobile */}
          <div className="hidden sm:block w-px h-5 bg-border-subtle" />

          {/* VPS status indicators — hidden on mobile */}
          <div className="hidden sm:flex items-center gap-3 md:gap-5 text-xs text-text-muted">
            <div className="flex items-center gap-1.5">
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${connected ? 'bg-success' : 'bg-error'}`} />
              <span>{connected ? 'Connected' : 'Offline'}</span>
            </div>
            <span className="hidden md:inline">{formatSyncTime(lastSync)}</span>
            <span>${todayCost.toFixed(2)} today</span>
          </div>
        </div>
      </header>

      <SearchModal />
    </>
  );
}
